// Decoded server messages → world store, tick clock, app events and the client event bus
// (architecture §9).
// - welcome: store.welcome(w), clock.setTickRate(w.tickRate), sink.welcome(w).
// - frame: clock.onFrame(tick, atMs), store.applyFrame(msg); the first FRAME after WELCOME →
//   sink.firstFrame(); the first FRAME with OWN after SPAWNED → sink.playing(playerId).
// - spawned: store.spawned(id). death: store.died(msg), sink.death(msg).
// - notice: feedResumed → store.clear() first; every notice → sink.notice(msg).
// - cashOutState (money rooms): store.applyCashOutState(msg, atMs) (any player's ring).
// - cashOutDone (money rooms): store.cashedOut(msg), sink.cashOutDone?.(msg) — the life ended
//   with a payout; like DEATH, but the app shows the cash-out screen.
// - party, matchResult, serverError: forwarded.
//
// With a client bus (5th argument) every one of these also emits its events/bus.ts event, and
// every intent written to the socket (GameSocket `sent`) becomes splitSent / ejectSent / … with
// the own-cell snapshot the audio gating needs. Bus events are emitted after the store and the
// sink have seen the message, so listeners read the updated store.
//
// Implementer: client net/game. Consumer: app.ts (implements GameSink, calls attachHandlers once
// per GameSocket).

import {
  C2S,
  type CashOutDoneMessage,
  type DeathMessage,
  type ErrorMessage,
  type FrameMessage,
  type MatchResultMessage,
  NOTICE,
  type NoticeMessage,
  type PartyMessage,
  type WelcomeMessage,
} from '@ballio/shared';
import { type ClientBus, moneyErrorKind, NO_PAYLOAD, type OwnSnapshot } from '../events/bus.js';
import type { TickClock } from '../game/clock.js';
import type { WorldStore } from '../game/world-store.js';
import type { GameSocket } from './socket.js';

/** App-level reactions (ui-flow §2 transitions are driven from these). */
export interface GameSink {
  welcome(w: WelcomeMessage): void;
  /** The first FRAME of this socket arrived (boot → home). */
  firstFrame(): void;
  /** SPAWNED and then a FRAME with OWN were applied: the life is on screen (home → playing). */
  playing(playerId: number): void;
  death(msg: DeathMessage): void;
  notice(msg: NoticeMessage): void;
  party(msg: PartyMessage): void;
  matchResult(msg: MatchResultMessage): void;
  serverError(msg: ErrorMessage): void;
  /** Money rooms: the life was cashed out (playing → cash-out screen). Optional for old sinks. */
  cashOutDone?(msg: CashOutDoneMessage): void;
  /**
   * Arena rooms (WELCOME.arena): every FRAME, after the store, with its arrival time (the arena
   * client applies the arena sections). Optional; cell rooms never need it.
   */
  frame?(msg: FrameMessage, atMs: number): void;
}

/** Progress at or above this on a stopped hold means it completed (the server sends 1). */
const HOLD_COMPLETE = 0.9999;

/** The own-cell snapshot for an intent (newest FRAME). */
function ownSnapshot(store: WorldStore): OwnSnapshot {
  const n = store.ownNewest();
  return n ? { ownCells: n.count, maxOwnMass: n.massMax } : { ownCells: 0, maxOwnMass: 0 };
}

/**
 * Subscribes to `socket.events`; returns a function that unsubscribes everything. `bus`
 * (optional) receives the events/bus.ts net and intent events.
 */
export function attachHandlers(
  socket: GameSocket,
  store: WorldStore,
  clock: TickClock,
  sink: GameSink,
  bus?: ClientBus,
): () => void {
  /** A FRAME has arrived on the current socket. */
  let framed = false;
  /** SPAWNED arrived and its first FRAME with OWN has not yet (0 = none). */
  let spawning = 0;
  /** Own cell count after the last OWN (for ownCellsChanged). */
  let ownCount = 0;
  /**
   * The life that just ended (DEATH / CASH_OUT_DONE): its hold's final CASH_OUT_STATE may arrive
   * after the end (CASH_OUT_DONE is sent before the hold's last state), and is still "own".
   */
  let endedLife = 0;

  const offs = [
    socket.events.on('open', () => {
      framed = false;
      spawning = 0;
      ownCount = 0;
    }),
    socket.events.on('welcome', (w) => {
      framed = false;
      spawning = 0;
      ownCount = 0;
      endedLife = 0;
      store.welcome(w);
      clock.setTickRate(w.tickRate);
      sink.welcome(w);
      bus?.emit('welcome', { msg: w, money: w.money ?? null });
    }),
    socket.events.on('frame', ({ msg, atMs }) => {
      clock.onFrame(msg.tick, atMs);
      const valueBefore = bus ? store.ownValue : null;
      store.applyFrame(msg);
      sink.frame?.(msg, atMs);
      if (!framed) {
        framed = true;
        sink.firstFrame();
      }
      let nowPlaying = 0;
      // Arena rooms send ARENA_OWN instead of OWN while a life is on screen.
      if (spawning !== 0 && (msg.own || msg.arenaOwn)) {
        nowPlaying = spawning;
        spawning = 0;
        sink.playing(nowPlaying);
      }
      if (!bus) return;
      if (nowPlaying !== 0) bus.emit('playing', { playerId: nowPlaying });
      if (msg.own && store.own.playerId !== 0) {
        const count = store.own.cellIds.size;
        if (count !== ownCount) {
          const prev = ownCount;
          ownCount = count;
          bus.emit('ownCellsChanged', { count, prev });
        }
      }
      const lb = msg.leaderboard;
      if (lb) {
        bus.emit('leaderboard', {
          section: lb,
          yourRank: lb.yourRank,
          yourAmount: lb.yourAmount ?? null,
          amountKind: lb.amountKind ?? null,
          tick: msg.tick,
        });
      }
      if (msg.money) {
        const value = store.ownValue;
        bus.emit('moneyUpdate', { solUsd: store.money.solUsd, ownValue: value });
        if (value !== null && value !== valueBefore) {
          bus.emit('ownValueChanged', { value, prev: valueBefore });
        }
      }
    }),
    socket.events.on('spawned', (m) => {
      store.spawned(m.playerId);
      spawning = m.playerId;
      ownCount = 0;
      endedLife = 0;
      bus?.emit('spawned', { playerId: m.playerId });
    }),
    socket.events.on('death', (m) => {
      endedLife = store.own.playerId;
      store.died(m);
      spawning = 0;
      ownCount = 0;
      sink.death(m);
      bus?.emit('ownDeath', { msg: m, valueLost: m.valueLost ?? null });
    }),
    socket.events.on('notice', (m) => {
      if (m.code === NOTICE.FEED_RESUMED) store.clear();
      sink.notice(m);
      if (!bus) return;
      bus.emit('notice', m);
      if (m.code === NOTICE.CASH_OUT_TOO_EARLY) {
        const s = Number(m.arg);
        bus.emit('cashOutTooEarly', { secondsLeft: Number.isFinite(s) && s > 0 ? s : 0 });
      } else if (m.code === NOTICE.PAYOUT_DELAYED) {
        bus.emit('payoutDelayed', NO_PAYLOAD);
      }
    }),
    socket.events.on('cashOutState', ({ msg, atMs }) => {
      const prev = store.applyCashOutState(msg, atMs);
      if (!bus) return;
      const own =
        msg.playerId !== 0 && (msg.playerId === store.own.playerId || msg.playerId === endedLife);
      bus.emit('cashOutState', {
        playerId: msg.playerId,
        own,
        active: msg.active,
        progress: msg.progress,
        atMs,
      });
      if (!own) return;
      if (msg.active) {
        if (prev === null) bus.emit('ownCashOutStart', { progress: msg.progress });
        else bus.emit('ownCashOutProgress', { progress: msg.progress });
      } else if (prev !== null || msg.progress >= HOLD_COMPLETE) {
        bus.emit('ownCashOutStop', {
          progress: msg.progress,
          completed: msg.progress >= HOLD_COMPLETE,
        });
      }
    }),
    socket.events.on('cashOutDone', (m) => {
      endedLife = store.own.playerId;
      store.cashedOut(m);
      spawning = 0;
      ownCount = 0;
      sink.cashOutDone?.(m);
      bus?.emit('cashOutDone', { msg: m, value: m.value, solUsd: m.solUsd });
    }),
    socket.events.on('party', (m) => sink.party(m)),
    socket.events.on('matchResult', (m) => sink.matchResult(m)),
    socket.events.on('serverError', (m) => {
      sink.serverError(m);
      if (!bus) return;
      const kind = moneyErrorKind(m.code);
      bus.emit('serverError', { msg: m, money: kind });
      if (kind !== null) bus.emit('moneyError', { code: m.code, kind, message: m.message });
    }),
  ];

  if (bus) {
    offs.push(
      socket.events.on('sent', ({ op, ticket }) => {
        switch (op) {
          case C2S.SPLIT:
            bus.emit('splitSent', ownSnapshot(store));
            return;
          case C2S.EJECT:
            bus.emit('ejectSent', ownSnapshot(store));
            return;
          case C2S.PLAY:
            bus.emit('playSent', { ticket });
            return;
          case C2S.SPECTATE:
            bus.emit('spectateSent', NO_PAYLOAD);
            return;
          case C2S.SPECIAL:
            bus.emit('specialSent', NO_PAYLOAD);
            return;
          case C2S.CASH_OUT_START:
            bus.emit('cashOutStartSent', NO_PAYLOAD);
            return;
          case C2S.CASH_OUT_CANCEL:
            bus.emit('cashOutCancelSent', NO_PAYLOAD);
            return;
        }
      }),
    );
  }

  return () => {
    for (const off of offs) off();
    offs.length = 0;
  };
}
